import React, { useEffect } from "react";
import { connect } from "react-redux";

import { TerritoryLevel } from "@mapmycustomers/shared";
import CountryCode from "@mapmycustomers/shared/enum/CountryCode";

import { ensureDemographicDataLoaded, setZoomLevel } from "@app/scene/map/store/actions";
import {
  getDemographicDataConfiguration,
  isDemographicDataLoading,
} from "@app/scene/map/store/layers/boundaries/selectors";
import { DemographicDataConfiguration } from "@app/scene/map/store/layers/boundaries/types/BoundariesLayerData";
import { getMapViewport } from "@app/scene/map/store/selectors";
import useDemographicRegionsBoundaries from "@app/scene/map/utils/useDemographicRegionsBoundaries";
import { RootState } from "@app/store/rootReducer";
import MapViewportState from "@app/types/map/MapViewportState";
import { DEMOGRAPHY_FEATURE_ZINDEX } from "@app/util/map/consts";

import DemographyFeature from ".";

interface Props {
  countryCode: CountryCode;
  demographicDataConfiguration: DemographicDataConfiguration;
  demographicDataLoading: boolean;
  disableLabel?: boolean;
  level?: TerritoryLevel;
  onEnsureDemographicDataLoaded: typeof ensureDemographicDataLoaded.request;
  onZoomLevelChange: typeof setZoomLevel;
  viewport: MapViewportState;
}

const DemographyFeaturesLayer: React.FC<Props> = ({
  countryCode,
  demographicDataConfiguration,
  demographicDataLoading,
  disableLabel,
  level,
  onEnsureDemographicDataLoaded,
  onZoomLevelChange,
  viewport,
}) => {
  const [currentLevel, regions] = useDemographicRegionsBoundaries(
    countryCode,
    demographicDataConfiguration.onlyVisibleRegions,
    viewport
  );

  useEffect(() => {
    onEnsureDemographicDataLoaded();
  }, [onEnsureDemographicDataLoaded]);

  useEffect(() => {
    onZoomLevelChange(level ?? currentLevel);
  }, [currentLevel, level, onZoomLevelChange]);

  return !demographicDataLoading ? (
    <>
      <DemographyFeature
        currentLevel={level ?? currentLevel}
        disableLabel={disableLabel}
        level={TerritoryLevel.ADM1}
        regions={regions.adm1}
        type="demographicRegions-adm1"
        zIndex={DEMOGRAPHY_FEATURE_ZINDEX + 2}
      />
      <DemographyFeature
        currentLevel={level ?? currentLevel}
        disableLabel={disableLabel}
        level={TerritoryLevel.ADM2}
        regions={regions.adm2}
        type="demographicRegions-adm2"
        zIndex={DEMOGRAPHY_FEATURE_ZINDEX + 1}
      />
      <DemographyFeature
        currentLevel={level ?? currentLevel}
        disableLabel={disableLabel}
        level={TerritoryLevel.ADM3}
        regions={regions.adm3}
        type="demographicRegions-adm3"
        zIndex={DEMOGRAPHY_FEATURE_ZINDEX}
      />
    </>
  ) : null;
};

const mapDispatchToProps = {
  onEnsureDemographicDataLoaded: ensureDemographicDataLoaded.request,
  onZoomLevelChange: setZoomLevel,
};

const mapStateToProps = (state: RootState) => ({
  demographicDataConfiguration: getDemographicDataConfiguration(state),
  demographicDataLoading: isDemographicDataLoading(state),
  viewport: getMapViewport(state),
});

export default connect(mapStateToProps, mapDispatchToProps)(DemographyFeaturesLayer);
